import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import { profileAPI } from "../services/api";
import { CONTENT_LIMITS, EARLY_ACCESS_LIMITS } from "../constants/contentLimits";
import { BadgeCheck, Leaf, Sparkles } from "lucide-react";
import Button from "../components/Button";
import Prompt from "../components/Prompt";
import "./Plans.css";

const Plans = () => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const { updateUser } = useAuth();
  const { addToast } = useToasts();
  const [upgrading, setUpgrading] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isEarlyAccess = user?.tier === "early_access";

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      await profileAPI.upgradeTier();
      updateUser({ tier: "early_access" });
      addToast("Welcome to Early Access!", "success-info", 6000, true);
      navigate("/profile");
    } catch (error) {
      if (error.response?.status === 400) {
        addToast("You're already on Early Access", "success-info");
      } else {
        addToast("Failed to upgrade", "error-warning");
      }
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await profileAPI.cancelTier();
      updateUser({ tier: "free" });
      addToast("Reverted to Free plan", "success-info", 6000, true);
      setShowCancelPrompt(false);
      navigate("/profile");
    } catch {
      addToast("Failed to cancel", "error-warning");
    } finally {
      setCancelling(false);
    }
  };

  const formatNumber = (n) => n.toLocaleString();

  return (
    <div className="plans-container">
      <h1 className="plans-title">Plans</h1>
      <div className="plans-grid">
        <div
          className={`plans-card ${
            !isEarlyAccess ? "plans-card-active plans-card-current" : ""
          }`}
        >
          <span className="plans-current-badge">
            {!isEarlyAccess ? <><BadgeCheck size={14} />Current plan</> : <Leaf size={14} />}
          </span>
          <h2 className="plans-card-name">Free</h2>
          <p className="plans-card-price plans-card-price-spacer">Always free</p>
          <ul className="plans-features">
            <li>Lesson body up to {formatNumber(CONTENT_LIMITS.lessonBody)} characters</li>
            <li>Total content up to {formatNumber(CONTENT_LIMITS.totalBodyPerUser)} characters</li>
            <li>{CONTENT_LIMITS.maxOwnedCommunities} active community</li>
            <li>{CONTENT_LIMITS.connectionDailyNotes} conversation notes per day</li>
            <li>{CONTENT_LIMITS.acceptedConnectionDailyNotes} accepted connection notes per day</li>
            <li>{CONTENT_LIMITS.connectionThreads} visible connection threads</li>
            <li>Notes, flashcards, bookmarks, highlights</li>
            <li>Share links &amp; community sharing</li>
            <li>Export your data</li>
          </ul>
        </div>

        <div
          className={`plans-card ${
            isEarlyAccess ? "plans-card-active plans-card-current" : ""
          }`}
        >
          <span className="plans-current-badge">
            {isEarlyAccess ? <><BadgeCheck size={14} />Current plan</> : <Sparkles size={14} />}
          </span>
          <h2 className="plans-card-name">Early Access</h2>
          <p className="plans-card-price">Free during early access</p>
          <ul className="plans-features">
            <li>Lesson body up to {formatNumber(EARLY_ACCESS_LIMITS.lessonBody)} characters</li>
            <li>Total content up to {formatNumber(EARLY_ACCESS_LIMITS.totalBodyPerUser)} characters</li>
            <li>Up to {EARLY_ACCESS_LIMITS.maxOwnedCommunities} active communities</li>
            <li>{EARLY_ACCESS_LIMITS.connectionDailyNotes} conversation notes per day</li>
            <li>{EARLY_ACCESS_LIMITS.acceptedConnectionDailyNotes} accepted connection notes per day</li>
            <li>{EARLY_ACCESS_LIMITS.connectionThreads} visible connection threads</li>
            <li>{EARLY_ACCESS_LIMITS.connectionThreadNotes} recent notes per connection</li>
            <li>Everything in Free, plus higher limits</li>
          </ul>
          {isEarlyAccess ? (
            <>
              <button
                className="plans-cancel-link"
                onClick={() => setShowCancelPrompt(true)}
              >
                Cancel Early Access
              </button>
            </>
          ) : (
            <Button
              className="button-btn button-btn-blue"
              onClick={handleUpgrade}
              disabled={upgrading}
            >
              {upgrading ? "Upgrading..." : "Upgrade to Early Access"}
            </Button>
          )}
        </div>
      </div>

      {showCancelPrompt && (
        <Prompt
          question={<>Cancel Early Access and return to Free plan?<br/>Your existing content stays.<br/>Free limits will apply to new content.</>}
          onYes={handleCancel}
          onNo={() => setShowCancelPrompt(false)}
          yesLabel={cancelling ? "Cancelling..." : "Yes, cancel"}
          noLabel="Keep Early Access"
          yesButtonClass="button-middle button-middle-red"
          noButtonClass="button-middle button-middle-blue"
        />
      )}
    </div>
  );
};

export default Plans;
